import { type Ref, computed, ref } from "vue";
import { useStore } from "vuex";

import { usePromiseWrapper } from "@/hooks/common";
import contractBuilderService from "@/services/modules/contractBuilder";
import offerTemplateService from "@/services/modules/offerTemplate";
import { useContractBuilderStore } from "@/stores/contractBuilder";

import type { IPaginatedResponse } from "@/models/common";
import type { GetOfferTemplatesParams, OfferTemplate } from "@/models/offers";
import type {
  ContractTemplateFieldAndCategory,
  ContractTemplateOptions,
  ContractTemplateRecipientRole,
} from "@/models/options";

export const useContractBuilder = () => {
  const { getters } = useStore();
  const store = useContractBuilderStore();

  const contractTemplatesOptions = computed<ContractTemplateOptions | null>(
    () => getters["options/contractTemplates"]
  );

  const roleOptions = computed(() => {
    return Object.values(
      contractTemplatesOptions.value?.recipient_roles || []
    ).reduce<
      Partial<
        Record<ContractTemplateRecipientRole, ContractTemplateRecipientRole>
      >
    >((acc, role) => {
      acc[role] = role;
      return acc;
    }, {});
  });

  const templateDropdownOptions = computed(() => {
    const options =
      store.templates?.data?.reduce<Record<string, string>>(
        (acc, template) => ({
          ...acc,
          [template.id]: template.name,
        }),
        {}
      ) || {};
    return options;
  });

  const isLendflowContractTemplate = (
    searchedId: string | number,
    option?: string | { is_lendflow_template: boolean }
  ) => {
    if (!option) {
      return false;
    }

    if (typeof option === "string") {
      return store.templates.data.find(
        ({ id }) => Number(id) === Number(searchedId)
      )?.is_lendflow_template;
    } else if (
      Object.prototype.hasOwnProperty.call(option, "is_lendflow_template")
    ) {
      return option.is_lendflow_template;
    }
  };

  return {
    isLendflowContractTemplate,
    roleOptions,
    contractTemplatesOptions,
    templateDropdownOptions,
  };
};

export const useOfferTemplateFields = (): {
  offerTemplates: Ref<OfferTemplate[]>;
  contractTemplateFields: Ref<ContractTemplateFieldAndCategory[]>;
  isOfferTemplatesLoading: Ref<boolean>;
  isContractTemplateFieldsLoading: Ref<boolean>;
  fetchOfferTemplates: (
    params?: GetOfferTemplatesParams
  ) => Promise<IPaginatedResponse<OfferTemplate>>;
  fetchContractTemplateFields: (offerTemplateId: string) => Promise<void>;
  clearContractTemplateFields: () => void;
} => {
  const offerTemplates = ref<OfferTemplate[]>([]);
  const contractTemplateFields = ref<ContractTemplateFieldAndCategory[]>([]);

  const {
    loading: isOfferTemplatesLoading,
    fetchWrapper: fetchOfferTemplates,
  } = usePromiseWrapper(
    async (
      params?: GetOfferTemplatesParams
    ): Promise<IPaginatedResponse<OfferTemplate>> => {
      const response = await offerTemplateService.getTemplates(params);
      if (response.data) {
        offerTemplates.value = response.data;
      }
      return response;
    }
  );

  const {
    loading: isContractTemplateFieldsLoading,
    fetchWrapper: fetchContractTemplateFields,
  } = usePromiseWrapper(async (offerTemplateId: string): Promise<void> => {
    if (!offerTemplateId) {
      contractTemplateFields.value = [];
    }
    const categoryFields =
      await contractBuilderService.getOfferTemplateContractFields(
        offerTemplateId
      );
    contractTemplateFields.value = categoryFields;
  });

  const clearContractTemplateFields = () => {
    contractTemplateFields.value = [];
  };

  return {
    offerTemplates,
    contractTemplateFields,
    isOfferTemplatesLoading,
    isContractTemplateFieldsLoading,
    fetchOfferTemplates,
    fetchContractTemplateFields,
    clearContractTemplateFields,
  };
};
